import React, { FC } from 'react';

import { config, yup } from 'data';
import { paymentService } from 'services';
import { useForm, useLang, useMutation } from 'hooks';
import { Alert, Form, Input, Modal } from 'components/ui';
import { Nullish } from 'types/common';
import { ModalBaseProps } from 'types/components';
import { PaymentCardPinVerificationParams } from 'types/services';

type FormValues = PaymentCardPinVerificationParams;

const initialValues: FormValues = {
  pin: '',
};

const validationSchema = yup.object({
  pin: yup.string().required().trim().length(config.PAYMENT_PIN_LENGTH),
});

type PinVerificationModalProps = ModalBaseProps & {
  trxRef: string;
  error?: Nullish<string>;
  pending?: boolean;
  onSuccess?: VoidFunction;
};

const PinVerificationModal: FC<PinVerificationModalProps> = ({
  trxRef,
  error,
  pending,
  onSuccess,
  ...props
}) => {
  const form = useForm<FormValues>();
  const lang = useLang();

  const verifyCardPaymentPinMutation = useMutation({
    mutationFn: (values: FormValues) => paymentService.verifyCardPaymentPin(trxRef, values),
    onSuccess,
  });

  const handleSubmit = async (values: FormValues) => {
    await verifyCardPaymentPinMutation.mutateAsync(values);

    return false;
  };

  const loading = pending || verifyCardPaymentPinMutation.isPending;

  return (
    <Modal
      title={lang.get('invoicePayment.pinModal.title')}
      caption={lang.get('invoicePayment.pinModal.caption')}
      okText={lang.get('common.actions.continue')}
      okButtonProps={{ block: true }}
      cancelButtonProps={{ hidden: true }}
      width="small"
      confirmLoading={loading}
      closable={false}
      maskClosable={false}
      onOk={form.submit}
      {...props}
    >
      <Form
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        disabled={loading}
        onFinish={handleSubmit}
      >
        <Form.Item hidden={!error}>
          <Alert type="error" message={error} />
        </Form.Item>
        <Form.Item name="pin">
          <Input
            placeholder={lang.get('common.form.pin.placeholder')}
            minLength={config.PAYMENT_PIN_LENGTH}
            maxLength={config.PAYMENT_PIN_LENGTH}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PinVerificationModal;
